/**
 * Formulaire de changement de mot de passe
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Formulaire pour changer le mot de passe avec validation de force.
 */

  // Client Component pour Next.js

import { useState } from 'react';
import { usePassword, usePasswordStrength, usePasswordMatch } from '../../hooks';
import { PasswordStrengthMeter } from '../ui/PasswordStrengthMeter';
import { ButtonSpinner } from '../ui/LoadingSpinner';

/**
 * Props du composant ChangePasswordForm
 */
export interface ChangePasswordFormProps {
  /** ID de l'utilisateur */
  userId: string;
  /** Callback après changement réussi */
  onSuccess?: () => void;
  /** Callback en cas d'erreur */
  onError?: (error: string) => void;
  /** Afficher le bouton annuler */
  showCancel?: boolean;
  /** Callback du bouton annuler */
  onCancel?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant ChangePasswordForm
 *
 * @example
 * ```tsx
 * function PasswordSettings() {
 *   const { user } = useAuth();
 *
 *   return (
 *     <ChangePasswordForm
 *       userId={user.id}
 *       onSuccess={() => toast.success('Mot de passe modifié')}
 *     />
 *   );
 * }
 * ```
 */
export function ChangePasswordForm({
  userId,
  onSuccess,
  onError,
  showCancel = false,
  onCancel,
  className = '',
}: ChangePasswordFormProps) {
  const { changePassword, isLoading, error, clearError } = usePassword();

  const [formData, setFormData] = useState({
    ancienMotDePasse: '',
    nouveauMotDePasse: '',
    confirmationMotDePasse: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState({
    ancien: false,
    nouveau: false,
    confirmation: false,
  });

  // Hooks de validation
  const passwordStrength = usePasswordStrength(formData.nouveauMotDePasse);
  const passwordsMatch = usePasswordMatch(
    formData.nouveauMotDePasse,
    formData.confirmationMotDePasse
  );

  /**
   * Valider le formulaire
   */
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validation ancien mot de passe
    if (!formData.ancienMotDePasse) {
      errors.ancienMotDePasse = 'L\'ancien mot de passe est requis';
    }

    // Validation nouveau mot de passe
    if (!formData.nouveauMotDePasse) {
      errors.nouveauMotDePasse = 'Le nouveau mot de passe est requis';
    } else if (passwordStrength.score < 3) {
      errors.nouveauMotDePasse = 'Le mot de passe n\'est pas assez fort';
    }

    // Validation confirmation
    if (!formData.confirmationMotDePasse) {
      errors.confirmationMotDePasse = 'La confirmation est requise';
    } else if (!passwordsMatch) {
      errors.confirmationMotDePasse = 'Les mots de passe ne correspondent pas';
    }

    // Vérifier que le nouveau mot de passe est différent de l'ancien
    if (
      formData.ancienMotDePasse &&
      formData.nouveauMotDePasse &&
      formData.ancienMotDePasse === formData.nouveauMotDePasse
    ) {
      errors.nouveauMotDePasse = 'Le nouveau mot de passe doit être différent de l\'ancien';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Gérer la soumission du formulaire
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setFormErrors({});

    if (!validateForm()) {
      return;
    }

    try {
      await changePassword({
        idUtilisateur: userId,
        ancienMotDePasse: formData.ancienMotDePasse,
        nouveauMotDePasse: formData.nouveauMotDePasse,
        confirmerMotDePasse: formData.confirmationMotDePasse,
      });

      // Réinitialiser le formulaire
      setFormData({
        ancienMotDePasse: '',
        nouveauMotDePasse: '',
        confirmationMotDePasse: '',
      });

      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de changement de mot de passe';
      onError?.(message);
    }
  };

  /**
   * Gérer le changement de champ
   */
  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Effacer l'erreur du champ modifié
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * Basculer la visibilité d'un mot de passe
   */
  const toggleShowPassword = (field: keyof typeof showPasswords) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className={`w-full max-w-md ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* Titre */}
        <div>
          <h3 className="text-lg font-semibold text-content">
            Changer le mot de passe
          </h3>
          <p className="mt-1 text-sm text-content-muted">
            Assurez-vous que votre nouveau mot de passe est fort et unique
          </p>
        </div>

        {/* Erreur globale */}
        {error && (
          <div
            className="p-4 bg-danger-light border border-danger rounded-lg"
            role="alert"
          >
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        {/* Ancien mot de passe */}
        <div>
          <label
            htmlFor="ancien-password"
            className="block text-sm font-medium text-content-muted mb-1"
          >
            Ancien mot de passe
          </label>
          <div className="relative">
            <input
              id="ancien-password"
              type={showPasswords.ancien ? 'text' : 'password'}
              value={formData.ancienMotDePasse}
              onChange={handleChange('ancienMotDePasse')}
              disabled={isLoading}
              className={`w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                formErrors.ancienMotDePasse
                  ? 'border-danger'
                  : 'border-border'
              } bg-surface text-content`}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => toggleShowPassword('ancien')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content"
              tabIndex={-1}
            >
              {showPasswords.ancien ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {formErrors.ancienMotDePasse && (
            <p className="mt-1 text-sm text-danger">
              {formErrors.ancienMotDePasse}
            </p>
          )}
        </div>

        {/* Nouveau mot de passe */}
        <div>
          <label
            htmlFor="nouveau-password"
            className="block text-sm font-medium text-content-muted mb-1"
          >
            Nouveau mot de passe
          </label>
          <div className="relative">
            <input
              id="nouveau-password"
              type={showPasswords.nouveau ? 'text' : 'password'}
              value={formData.nouveauMotDePasse}
              onChange={handleChange('nouveauMotDePasse')}
              disabled={isLoading}
              className={`w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                formErrors.nouveauMotDePasse
                  ? 'border-danger'
                  : 'border-border'
              } bg-surface text-content`}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => toggleShowPassword('nouveau')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content"
              tabIndex={-1}
            >
              {showPasswords.nouveau ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {formErrors.nouveauMotDePasse && (
            <p className="mt-1 text-sm text-danger">
              {formErrors.nouveauMotDePasse}
            </p>
          )}

          {/* Indicateur de force */}
          {formData.nouveauMotDePasse && (
            <div className="mt-3">
              <PasswordStrengthMeter strength={passwordStrength} />
            </div>
          )}
        </div>

        {/* Confirmation mot de passe */}
        <div>
          <label
            htmlFor="confirmation-password"
            className="block text-sm font-medium text-content-muted mb-1"
          >
            Confirmer le nouveau mot de passe
          </label>
          <div className="relative">
            <input
              id="confirmation-password"
              type={showPasswords.confirmation ? 'text' : 'password'}
              value={formData.confirmationMotDePasse}
              onChange={handleChange('confirmationMotDePasse')}
              disabled={isLoading}
              className={`w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                formErrors.confirmationMotDePasse
                  ? 'border-danger'
                  : formData.confirmationMotDePasse && passwordsMatch
                  ? 'border-success'
                  : 'border-border'
              } bg-surface text-content`}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => toggleShowPassword('confirmation')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content"
              tabIndex={-1}
            >
              {showPasswords.confirmation ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {formErrors.confirmationMotDePasse && (
            <p className="mt-1 text-sm text-danger">
              {formErrors.confirmationMotDePasse}
            </p>
          )}
          {formData.confirmationMotDePasse && passwordsMatch && !formErrors.confirmationMotDePasse && (
            <p className="mt-1 text-sm text-success">
              ✓ Les mots de passe correspondent
            </p>
          )}
        </div>

        {/* Boutons */}
        <div className="flex gap-3">
          {showCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium text-content-muted bg-surface hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading || passwordStrength.score < 3}
            className="flex-1 flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-content-inverted bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <ButtonSpinner className="mr-2" />
                Modification...
              </>
            ) : (
              'Changer le mot de passe'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
